import { Image as ImageIcon, Upload, Video } from "lucide-react"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { Button } from "react-bootstrap"
import MediaPicker from "./MediaPicker"
import "../css/SingleMediaPicker.css"
import "../css/MediaPicker.css"

const DEFAULT_ALLOWED_KINDS = ["image", "video"]

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"]
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"]

const normalizeAllowedKinds = (allowedKinds) => {
	if (!Array.isArray(allowedKinds) || allowedKinds.length === 0) return ["image"]
	return allowedKinds.filter((kind) => kind === "image" || kind === "video")
}

const getDefaultKind = (allowedKinds, value) => {
	if (value?.kind && allowedKinds.includes(value.kind)) return value.kind
	if (allowedKinds.includes("image")) return "image"
	return "video"
}

const SingleMediaPicker = ({
	allowedKinds = DEFAULT_ALLOWED_KINDS,
	value = null,
	onChange,
	onError,
	maxSizeBytes,
	maxVideoDurationSec = 60,
	imageAccept = IMAGE_TYPES,
	videoAccept = VIDEO_TYPES,
	imageCaptureLabel = "Take photo",
	imageLibraryLabel = "Choose photo",
	videoCaptureLabel = "Record video",
	videoLibraryLabel = "Choose video",
	clearLabel = "Remove",
	buttonSize = "sm",
	previewHeight = 320,
	className = "",
	title = "Share your progress",
	subtitle = "Optional. Upload either a photo or a short video.",
	compact = false,
}) => {
	const resolvedKinds = useMemo(
		() => normalizeAllowedKinds(allowedKinds),
		[allowedKinds]
	)

	const [showOptions, setShowOptions] = useState(!compact || !!value)
	const [activeKind, setActiveKind] = useState(
		getDefaultKind(resolvedKinds, value)
	)

	useEffect(() => {
		setActiveKind((current) => {
			if (value?.kind && resolvedKinds.includes(value.kind)) return value.kind
			return resolvedKinds.includes(current) ? current : getDefaultKind(resolvedKinds, value)
		})
	}, [resolvedKinds, value])

	const showKindToggle = resolvedKinds.length > 1

	const handleKindChange = (nextKind) => {
		setShowOptions(true)
		if (nextKind === activeKind) return

		setActiveKind(nextKind)

		if (value) {
			onChange(null)
		}
	}

	const handleSelect = (file) => {
		if (!file) {
			onChange(null)
			return
		}

		onChange({
			kind: activeKind,
			file,
			name: file.name,
			isExisting: false,
			url: null,
		})
	}

	const handleClear = () => {
		onChange(null)
	}

	const currentValueFile =
		value?.kind === activeKind && value?.file instanceof File ? value.file : null

	const currentValueUrl =
		value?.kind === activeKind && value?.url ? value.url : null

	return (
		<div className={`single-media-picker ${className}`.trim()}>
			<div className={`single-media-picker-card border rounded-3 ${compact ? "p-3" : "p-4"} bg-light`}>
				<div className="single-media-picker-header text-center mb-3">
					{!compact && <Upload className="mb-2" size={24} />}
					<div className="single-media-picker-title">{title}</div>
					{subtitle && <div className="single-media-picker-subtitle">{subtitle}</div>}
				</div>

				{showKindToggle && (
					<div className="single-media-picker-toggle mb-3">
						<Button
							type="button"
							variant={activeKind === "image" && showOptions ? "primary" : "light"}
							aria-expanded={showOptions && activeKind === "image"}
							className="single-media-picker-toggle-btn"
							onClick={() => handleKindChange("image")}
						>
							<ImageIcon size={16} className="me-2" />
							Photo
						</Button>

						<Button
							type="button"
							variant={activeKind === "video" && showOptions ? "primary" : "light"}
							aria-expanded={showOptions && activeKind === "video"}
							className="single-media-picker-toggle-btn"
							onClick={() => handleKindChange("video")}
						>
							<Video size={16} className="me-2" />
							Video
						</Button>
					</div>
				)}

				{(!compact || showOptions || value || !showKindToggle) && <div className="single-media-picker-inner">
					{activeKind === "image" && (
						<MediaPicker
							kind="image"
							accept={imageAccept}
							maxSizeBytes={maxSizeBytes}
							captureLabel={imageCaptureLabel}
							libraryLabel={compact ? "Library" : imageLibraryLabel}
							valueFile={currentValueFile}
							valueUrl={currentValueUrl}
							onSelect={handleSelect}
							onError={onError}
							onClear={handleClear}
							clearLabel={clearLabel}
							buttonSize={buttonSize}
							previewHeight={previewHeight}
						/>
					)}

					{activeKind === "video" && (
						<MediaPicker
							kind="video"
							accept={videoAccept}
							maxSizeBytes={maxSizeBytes}
							maxDurationSec={maxVideoDurationSec}
							captureLabel={videoCaptureLabel}
							libraryLabel={compact ? "Library" : videoLibraryLabel}
							valueFile={currentValueFile}
							valueUrl={currentValueUrl}
							onSelect={handleSelect}
							onError={onError}
							onClear={handleClear}
							clearLabel={clearLabel}
							buttonSize={buttonSize}
							previewHeight={previewHeight}
						/>
					)}
				</div>}
			</div>
		</div>
	)
}

SingleMediaPicker.propTypes = {
	compact: PropTypes.bool,
	allowedKinds: PropTypes.arrayOf(
		PropTypes.oneOf(["image", "video"])
	),
	value: PropTypes.shape({
		kind: PropTypes.oneOf(["image", "video"]),
		file: PropTypes.any,
		name: PropTypes.string,
		isExisting: PropTypes.bool,
		url: PropTypes.string,
	}),
	onChange: PropTypes.func.isRequired,
	onError: PropTypes.func,
	maxSizeBytes: PropTypes.number.isRequired,
	maxVideoDurationSec: PropTypes.number,
	imageAccept: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.string,
	]),
	videoAccept: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.string,
	]),
	imageCaptureLabel: PropTypes.string,
	imageLibraryLabel: PropTypes.string,
	videoCaptureLabel: PropTypes.string,
	videoLibraryLabel: PropTypes.string,
	clearLabel: PropTypes.string,
	buttonSize: PropTypes.string,
	previewHeight: PropTypes.number,
	className: PropTypes.string,
	title: PropTypes.string,
	subtitle: PropTypes.string,
}

export default SingleMediaPicker